import Button, { NavigableButton } from '@carvana/ds-button';
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { DispatcherEvent } from '@carvana/merch-native';
import { datadogRum } from '@datadog/browser-rum';
import { type MouseEvent, useContext } from 'react';
import { ANALYTICS_EVENTS } from '~/constants/flp/analyticsEvents';
import { DATADOG_EVENTS } from '~/constants/flp/datadogEvents';
import { useIsNative } from '~/context/common/DeviceContext';
import { useFinancingContext } from '~/context/flp/FinancingContext';
import { useModalContext } from '~/context/flp/ModalContext/ModalContext';
import sendMessageToNative from '~/native/flp/dispatchers/sendMessageToNative';
import { getCvnaIdFinanceFilters } from '~/utilities/flp/getCvnaId';
import getOrdinalSuffix from '~/utilities/flp/getOrdinalSuffix';
import {
  CosignerContent,
  HorizontalRule,
  TermsCardContainer,
  TermsContent,
  TermsContentCopy,
  TermsContentHeader,
  TermsCta,
  TermsProgress,
  TermsProgressHeading
} from './TermsCard.styles';
import Timer from './Timer';

export function TermsCard() {
  const { financeTerms, inventory, coSigner } = useFinancingContext();
  const { setAddCoApplicantModalOpen } = useModalContext();
  const { trackEvent } = useContext(AnalyticsContext);
  const isNative = useIsNative();

  const cvnaid = getCvnaIdFinanceFilters({ sortBy: 'CashDue' });
  const expirationDate = new Date(financeTerms?.expiration ?? '');
  const hasInventory = Number(inventory) > 0;
  const isValidExpiration = !isNaN(Date.parse(financeTerms?.expiration ?? ''));

  let month: string | null = null;
  let day: string | null = null;
  let suffix: string | null = null;
  let daysRemaining: number | null = null;
  let percentage: number | null = null;

  if (isValidExpiration) {
    month = expirationDate.toLocaleDateString('en-US', { month: 'long' });
    day = expirationDate.getDate().toString();
    suffix = getOrdinalSuffix(expirationDate.getDate());

    // Use the start of today to calculate the days remaining
    const dayInMillis = 1000 * 60 * 60 * 24;
    const todayStart = new Date().setHours(0, 0, 0, 0);
    daysRemaining = Math.max(
      Math.floor((expirationDate.getTime() - todayStart) / dayInMillis),
      1
    );

    const maxCreditAppExpirationDays = 30;
    percentage = daysRemaining / maxCreditAppExpirationDays;
  }

  function handleClick(ev: MouseEvent<HTMLAnchorElement>) {
    datadogRum.addAction(DATADOG_EVENTS.PREQUALIFIED.CLICKS_HERO_CTA);
    trackEvent(ANALYTICS_EVENTS.PREQUALIFIED.CLICKS_HERO_CTA);

    if (isNative) {
      ev.preventDefault();
      sendMessageToNative(DispatcherEvent.SearchVehicles, { cvnaid });
    }
  }

  const handleAddCosigner = () => {
    datadogRum.addAction(DATADOG_EVENTS.PREQUALIFIED.CLICKS_ADD_COSIGNER_HERO);
    trackEvent(ANALYTICS_EVENTS.PREQUALIFIED.CLICKS_ADD_COSIGNER_HERO);
    setAddCoApplicantModalOpen(true);
  };

  return (
    <TermsCardContainer>
      <TermsContent>
        <TermsContentHeader>
          {isValidExpiration && daysRemaining! > 0 && (
            <Timer percentage={percentage!}>
              <TermsProgress>
                <TermsProgressHeading data-testid="days-remaining">
                  {daysRemaining}
                </TermsProgressHeading>
                <p>days</p>
              </TermsProgress>
            </Timer>
          )}
        </TermsContentHeader>
        <TermsContentCopy>
          {hasInventory ? (
            <p>
              You’ve unlocked real terms on{' '}
              <strong>{Number(inventory).toLocaleString('en-US')}</strong>{' '}
              vehicles until{' '}
              <span
                className="t-strong-sm sm:t-strong-md"
                data-testid="expiration-date"
              >
                {month}&nbsp;{day}
                {suffix}
              </span>
              &#46;
            </p>
          ) : (
            <h2>You can start shopping with real financing terms!</h2>
          )}
        </TermsContentCopy>
        <TermsCta>
          <NavigableButton
            className="w-full"
            data-analytics-id="Financing Shop With Your Terms Button"
            data-native-included={false}
            data-testid="shop-with-your-terms"
            href={`/cars/filters?cvnaid=${cvnaid}&refSource=financing`}
            onClick={handleClick}
          >
            Shop With Your Terms
          </NavigableButton>
        </TermsCta>
      </TermsContent>
      {/* NOTE(2024/12): Co-signer configuration should only appear for customers without state restrictions and without an attached cosigner. */}
      {coSigner !== null && !coSigner?.data && (
        <>
          <HorizontalRule />
          <CosignerContent>
            <p className="text-text-strong t-body-md">
              A co-signer can{' '}
              <span className="t-strong-md">improve your terms</span>.
            </p>
            <Button
              data-analytics-id="Financing Add A Co-Signer Button"
              variant="tertiary"
              onClick={handleAddCosigner}
              data-testid="add-co-signer"
            >
              Add a Co-signer
            </Button>
          </CosignerContent>
        </>
      )}
    </TermsCardContainer>
  );
}
